import { hexToWorld } from '../hex.ts';
import { isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot } from '../types.ts';
import { ev } from './combat.ts';
import { stepToward } from './enemies.ts';
import { railTrack } from './rails.ts';
import { climbTick, climbing } from './hoist.ts';

/**
 * Saga escort acts (docs/QUESTS.md): a caravan crosses the clearing from the far end of one road, through
 * the middle, out along another. There is no node to hold: every nightmare on the field comes for it
 * (a hero in the way still holds them), it stops while one is close, and the act is won when it reaches the end of its road.
 */
export const startEscort = (s: BattleState): void => {
  const r = s.rules?.escort;
  const [a, b] = s.map.sectors;
  if (!r || !a || !b) return;
  // In along the first road (its path runs from the far end to the gate), through the middle, out along the second.
  // The runaway carts (Las vías 1) run on their own rail instead, round the cavern (rails.ts).
  const rail = railTrack(s);
  const road = rail ? rail.road : [...a.path.map((h) => hexToWorld(h.q, h.r)), { x: 0, y: 0 }, ...b.path.slice().reverse().map((h) => hexToWorld(h.q, h.r))];
  const start = road[0] as { x: number; y: number };
  // It draws from anywhere on the clearing: across it, edge to edge.
  const edge = hexToWorld(s.map.radius, 0);
  const across = 2 * isqrt(edge.x * edge.x + edge.y * edge.y);
  const cart: Bot = {
    id: s.nextId++,
    owner: 0,
    pid: '',
    kind: 'cart',
    x: start.x,
    y: start.y,
    hp: r.hp,
    maxHp: r.hp,
    damage: 0,
    radius: 0,
    every: 0,
    cd: 0,
    expires: 0x7fffffff,
    taunt: across,
    aura: 0,
    auraBp: 0,
    facing: 0,
    anim: 'idle',
    animT: 0,
  };
  (s.bots ??= []).push(cart);
  s.escort = { cart: cart.id, road, next: 1, arrived: false, stopRange: r.stopRange, speed: s.rules?.climb ? 0 : r.speed };
  if (rail && s.rules?.rails) s.rails = { walls: [], weight: 0, down: false, bridge: rail.bridge, last: rail.last, next: s.rules.rails.first, derailed: 0 };
  // The heroes start beside it.
  s.heroes.forEach((h, i) => {
    h.x = h.destX = h.ax = start.x + (i - 1) * 600;
    h.y = h.destY = h.ay = start.y + 600;
    h.path = [];
    h.moving = false;
  });
};

/** The caravan's turn: on along its road unless a nightmare is on it. */
export const cartTick = (s: BattleState, b: Bot): void => {
  const e = s.escort;
  if (!e || e.arrived) return;
  // The bell's climb: its ropes and its pace, before it moves.
  climbTick(s);
  // (The runaway carts have no brake; the bell doesn't stop either: the nightmares cut its ropes instead, battle/hoist.ts.)
  if (!s.rails && !climbing(s) && s.enemies.some((o) => o.hp > 0 && o.kind !== 'boneWall' && withinRange(o.x, o.y, b.x, b.y, e.stopRange))) {
    b.anim = 'idle';
    return;
  }
  const to = e.road[e.next];
  if (!to) return;
  b.anim = 'walk';
  if (!stepToward(b, to.x, to.y, e.speed)) return;
  e.next++;
  if (e.next >= e.road.length) {
    e.arrived = true;
    ev(s, { t: 'boom', x: b.x, y: b.y, r: 900, kind: 'arrived' });
  }
};

/** Escort over: the caravan made it, or it broke. */
export const escortResult = (s: BattleState): 'won' | 'lost' | null => {
  const e = s.escort;
  if (!e) return null;
  if (e.arrived) return 'won';
  const cart = s.bots?.find((b) => b.id === e.cart);
  return !cart || cart.hp <= 0 ? 'lost' : null;
};
